import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useReactFlow } from 'reactflow';
import { Search } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Dialog, DialogContent } from '../ui/dialog';
import { useWorkflowStore } from '../../store/workflowStore';
import useCanvasNavStore from '../../store/canvasNavStore';
import { shortNodeNumber } from '../../utils/graphPaths';

/**
 * Buscador de nodos (Ctrl/Cmd+K): escribí un número ("21") o un nombre
 * ("Lumière") y el canvas se centra en ese nodo, lo resalta 2 s y lo
 * selecciona. Sólo navegación: no toca el grafo.
 */
const FOCUS_ZOOM = 1.1;

export const NodeSearchPalette = () => {
  const open = useCanvasNavStore((s) => s.searchOpen);
  const setOpen = useCanvasNavStore((s) => s.setSearchOpen);
  const flash = useCanvasNavStore((s) => s.flash);
  const nodes = useWorkflowStore((s) => s.nodes);
  const setSelectedNode = useWorkflowStore((s) => s.setSelectedNode);
  const { setCenter } = useReactFlow();
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  const items = useMemo(
    () =>
      nodes.map((node) => ({
        id: node.id,
        number: shortNodeNumber(node.id),
        name: node.data?.name || '',
        type: node.type,
        position: node.position,
        width: node.width,
        height: node.height,
      })),
    [nodes]
  );

  const goTo = useCallback(
    (item) => {
      const node = nodes.find((n) => n.id === item.id);
      if (!node) return;
      const x = (node.position?.x || 0) + (node.width || 320) / 2;
      const y = (node.position?.y || 0) + (node.height || 200) / 2;
      setCenter(x, y, { zoom: FOCUS_ZOOM, duration: 500 });
      setSelectedNode(node);
      flash(node.id);
      setOpen(false);
      setQuery('');
    },
    [nodes, setCenter, setSelectedNode, flash, setOpen]
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="node-search-dialog p-0"
        data-testid="node-search-palette"
      >
        <Command shouldFilter={false}>
          <div className="node-search-head">
            <Search size={15} />
            <CommandInput
              placeholder="Buscar nodo por número o nombre…"
              value={query}
              onValueChange={setQuery}
              data-testid="node-search-input"
            />
          </div>
          <CommandList>
            <CommandEmpty>Ningún nodo coincide.</CommandEmpty>
            <CommandGroup heading={`${items.length} nodos en el canvas`}>
              {items
                .filter((item) => {
                  const q = query.trim().toLowerCase();
                  if (!q) return true;
                  return (
                    item.number.startsWith(q) ||
                    item.name.toLowerCase().includes(q) ||
                    String(item.type || '').toLowerCase().includes(q)
                  );
                })
                .slice(0, 60)
                .map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    onSelect={() => goTo(item)}
                    data-testid={`node-search-result-${item.id}`}
                  >
                    <span className="node-search-num">#{item.number}</span>
                    <span className="node-search-name">
                      {item.name || <em>sin nombre</em>}
                    </span>
                    <span className="node-search-type">{item.type}</span>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export default NodeSearchPalette;
